import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";

import { SortStateDescriptor } from "components/Filters/types";
import {
  EmptystatePuzzleColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import TablePagination from "components/Table/Pagination";
import { PAGINATION_OPTIONS } from "components/Table/Pagination/constants";
import usePagination from "components/Table/Pagination/usePagination";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import TableSimple from "ds/components/TableSimple";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrder } from "types/generated";

import SPACE_CONTEXTS_CELLS from "./Cells";
import {
  SPACE_CONTEXTS_FILTERS_ORDER_KEY,
  SPACE_CONTEXTS_INITIAL_LIMIT,
  SPACE_CONTEXTS_INITIAL_ORDER_BY,
  SPACE_CONTEXTS_LIMIT_STORAGE_KEY,
  SPACE_CONTEXTS_SORT_CONFIG,
} from "./constants";
import useSpaceDetailsContexts from "./useSpaceDetailsContexts";
import useSpaceDetailsContextsTableStore from "./useStore";

type SpaceDetailsContextsProps = {
  spaceId: string;
};

const SpaceDetailsContexts = ({ spaceId }: SpaceDetailsContextsProps) => {
  const [searchParams] = useSearchParams();
  const [orderBy, setOrderBy] = useState<SearchQueryOrder>(SPACE_CONTEXTS_INITIAL_ORDER_BY);
  const [storedRowsPerPage] = useLocalStorage<number>(
    SPACE_CONTEXTS_LIMIT_STORAGE_KEY,
    SPACE_CONTEXTS_INITIAL_LIMIT
  );

  const first = storedRowsPerPage || SPACE_CONTEXTS_INITIAL_LIMIT;

  const fullTextSearch = getSearchQuery(searchParams) || null;

  const {
    loadContexts,
    error,
    contexts,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor,
    startCursor,
    paginationResetKey,
  } = useSpaceDetailsContexts({ spaceId, orderBy, first, fullTextSearch });

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadContexts,
    endCursor,
    startCursor,
    allItemsCount: totalCount,
    visibleItemsCount: contexts.length,
    initialLimit: SPACE_CONTEXTS_INITIAL_LIMIT,
    storageKey: SPACE_CONTEXTS_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleSortCallback = useCallback((descriptor: SortStateDescriptor) => {
    setOrderBy({ field: descriptor.sortKey, direction: descriptor.sortDirection });
  }, []);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (initialLoading) {
    return <PageLayoutSkeleton />;
  }

  return (
    <>
      <PageInfoHeaderWrapper>
        <PageInfoHeaderTitle title="Contexts" />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search by context name"
            filtersOrderSettingsKey={SPACE_CONTEXTS_FILTERS_ORDER_KEY}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>

      <ListContentWrapper>
        {!contexts.length && !fullTextSearch && (
          <EmptyState
            title="This space has no contexts yet"
            icon={EmptystatePuzzleColored}
            caption="Contexts assigned to this space will show up here."
          />
        )}

        {!contexts.length && !!fullTextSearch && (
          <EmptyState
            title="No contexts found"
            icon={EmptystateSearchNoResultsColored}
            caption="No contexts match your search."
          />
        )}

        {!!contexts.length && (
          <>
            <TableSimple
              data={contexts}
              loading={loadingContent}
              store={useSpaceDetailsContextsTableStore}
              ariaLabel="Space contexts table"
              rowsPerPage={rowsPerPage}
              sortCallback={handleSortCallback}
              cellPerColumn={SPACE_CONTEXTS_CELLS}
              sortConfig={SPACE_CONTEXTS_SORT_CONFIG}
              filtersStorageKey={SPACE_CONTEXTS_FILTERS_ORDER_KEY}
              hideSettings
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={totalCount}
              lastPage={lastPage}
              page={page}
              paginationOptions={PAGINATION_OPTIONS}
            />
          </>
        )}
      </ListContentWrapper>
    </>
  );
};

export default SpaceDetailsContexts;
